import React, { useEffect, useRef, useState } from "react";

/* ===================== CONFIG ===================== */

const SYMBOLS = [
  "EUR/USD",
  "GBP/USD",
  "USD/JPY",
  "AUD/USD",
  "BTC/USD",
  "ETH/USD",
  "BNB/USD",
  "SOL/USD",
];

const API =
  "https://us-central1-million2trillion-4d06a.cloudfunctions.net/marketQuote";

/* SAFE FALLBACK */
const FALLBACK_MARKET = {
  "EUR/USD": { price: 1.18, change: 0.0 },
  "GBP/USD": { price: 1.36, change: -0.01 },
  "USD/JPY": { price: 157.1, change: 0.02 },
  "AUD/USD": { price: 0.7, change: -0.01 },
  "BTC/USD": { price: 69300, change: 0.08 },
  "ETH/USD": { price: 2085, change: -0.05 },
  "BNB/USD": { price: 310, change: 0.12 },
  "SOL/USD": { price: 145, change: -0.2 },
};

const STATS = [
  { value: "630,000+", label: "Active Traders" },
  { value: "3,000+", label: "Active Brokers" },
  { value: "6,500+", label: "Active Investors" },
  { value: "1,571+", label: "Active Mentors" },
  { value: "9,000+", label: "Market Tools" },
  { value: "20,000+", label: "Resources" },
  { value: "100+", label: "Countries" },
  { value: "200+", label: "Verified Roles" },
];

const FEED_MESSAGES = [
  { name: "Lucas", role: "Quant Analyst", location: "Brazil" },
  { name: "John", role: "FX Trader", location: "London" },
  { name: "Amit", role: "Crypto Investor", location: "India" },
  { name: "Sarah", role: "Fund Manager", location: "New York" },
  { name: "David", role: "Algo Trader", location: "Singapore" },

  { name: "Ahmed", role: "Commodities Trader", location: "UAE" },
  { name: "Maria", role: "Portfolio Manager", location: "Spain" },
  { name: "Chen", role: "Equity Analyst", location: "Hong Kong" },
  { name: "Rajesh", role: "Derivatives Trader", location: "Mumbai" },
  { name: "Daniel", role: "Risk Manager", location: "Toronto" },

  { name: "Sophie", role: "Investment Advisor", location: "Paris" },
  { name: "Michael", role: "Hedge Fund Analyst", location: "Chicago" },
  { name: "Hassan", role: "Crypto Trader", location: "Qatar" },
  { name: "Priya", role: "Wealth Manager", location: "Bangalore" },
  { name: "Tom", role: "Macro Strategist", location: "Sydney" },

  { name: "Yuki", role: "FX Options Trader", location: "Tokyo" },
  { name: "Carlos", role: "Private Equity Analyst", location: "Mexico City" },
  { name: "Elena", role: "Fixed Income Analyst", location: "Frankfurt" },
  { name: "Samuel", role: "Prop Trader", location: "Johannesburg" },
  { name: "Ivan", role: "Crypto Researcher", location: "Berlin" },

  { name: "Nikhil", role: "Swing Trader", location: "Pune" },
  { name: "Fatima", role: "Shariah Finance Advisor", location: "Riyadh" },
  { name: "Andrew", role: "ETF Strategist", location: "Boston" },
  { name: "Wei", role: "Quant Developer", location: "Shanghai" },
  { name: "Noah", role: "Broker Representative", location: "USA" },
];

/* ===================== COMPONENT ===================== */

export default function MarketStatsFeedSection() {
  const [market, setMarket] = useState({});
  const lastGoodMarket = useRef({});
  const marketScrollRef = useRef(null);

  const [feedIndex, setFeedIndex] = useState(0);
  const statsScrollRef = useRef(null);

  /* -------- FETCH MARKET -------- */
  const fetchMarket = async () => {
    const updates = {};

    await Promise.all(
      SYMBOLS.map(async (symbol) => {
        try {
          const res = await fetch(`${API}?symbol=${symbol}`);
          const json = await res.json();

          if (json?.code === 429) return;

          const price = Number(json.price || json.close);
          if (!price) return;

          updates[symbol] = {
            price,
            change: Number(json.percent_change || 0),
          };
        } catch {}
      }),
    );

    if (Object.keys(updates).length) {
      lastGoodMarket.current = { ...lastGoodMarket.current, ...updates };
      setMarket(lastGoodMarket.current);
    }
  };

  useEffect(() => {
    fetchMarket();
    const i = setInterval(fetchMarket, 30000);
    return () => clearInterval(i);
  }, []);

  /* -------- MARKET SCROLL -------- */
  useEffect(() => {
    const el = marketScrollRef.current;
    if (!el) return;
    let y = 0;
    const tick = () => {
      y += 0.4;
      if (y >= el.scrollHeight / 2) y = 0;
      el.scrollTop = y;
      requestAnimationFrame(tick);
    };
    tick();
  }, []);

  /* -------- STATS SCROLL -------- */
  useEffect(() => {
    const el = statsScrollRef.current;
    if (!el) return;
    let y = 0;
    const tick = () => {
      y += 0.35;
      if (y >= el.scrollHeight / 2) y = 0;
      el.scrollTop = y;
      requestAnimationFrame(tick);
    };
    tick();
  }, []);

  /* -------- FEED ROTATE -------- */
  useEffect(() => {
    const i = setInterval(
      () => setFeedIndex((i) => (i + 1) % FEED_MESSAGES.length),
      2500,
    );
    return () => clearInterval(i);
  }, []);

  const dataToShow =
    Object.keys(market).length > 0
      ? market
      : Object.keys(lastGoodMarket.current).length > 0
        ? lastGoodMarket.current
        : FALLBACK_MARKET;

  /* ===================== UI ===================== */

  return (
    <section className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 py-24 md:grid-cols-[1fr_1fr_1fr]">
      {/* LIVE MARKETS */}
      <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-[#121826] to-[#0b0f1a] p-5 shadow-[0_0_40px_rgba(0,0,0,0.6)]">
        <div className="mb-4 flex items-center gap-2 text-xs tracking-widest text-gray-400">
          {/* Blinking green dot */}
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400"></span>
          </span>
          <span>LIVE MARKETS</span>
        </div>
        <div
          ref={marketScrollRef}
          className="no-scrollbar h-[220px] overflow-hidden"
        >
          {[...SYMBOLS, ...SYMBOLS].map((s, i) => {
            const q = dataToShow[s];
            const up = q.change >= 0;
            return (
              <div
                key={s + i}
                className="flex h-[44px] items-center justify-between text-sm"
              >
                <span className="font-normal text-white">{s}</span>
                <div className="flex items-center gap-3">
                  <span className="text-white">{q.price.toLocaleString()}</span>
                  <span
                    className={`text-xs ${
                      up ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {up ? "▲" : "▼"} {q.change.toFixed(2)}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* PLATFORM STATS */}
      <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-[#121826] to-[#0b0f1a] p-5 shadow-[0_0_40px_rgba(0,0,0,0.6)]">
        <div className="mb-4 flex items-center gap-2 text-xs tracking-widest text-gray-400">
          {/* Blinking green dot */}
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-green-400"></span>
          </span>
          <span>PLATFORM STATS</span>
        </div>

        <div
          ref={statsScrollRef}
          className="no-scrollbar h-[220px] overflow-hidden"
        >
          {[...STATS, ...STATS].map((s, i) => (
            <div key={i} className="flex h-[44px] items-center justify-between">
              <span className="text-sm font-normal text-white">{s.label}</span>
              <span className="text-lg font-semibold text-green-400">
                {s.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* LIVE FEED */}
      <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-[#121826] to-[#0b0f1a] p-5 shadow-[0_0_40px_rgba(0,0,0,0.6)]">
        {/* Title with online indicator */}
        <div className="mb-4 flex items-center gap-2 text-xs tracking-widest text-gray-400">
          <span className="relative h-2 w-2 animate-pulse rounded-full bg-green-400" />
          VERIFIED USERS ONLINE
        </div>

        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => {
            const item = FEED_MESSAGES[(feedIndex + i) % FEED_MESSAGES.length];

            return (
              <div
                key={i}
                className="rounded-lg bg-white/10 px-3 py-2 text-sm backdrop-blur-sm"
              >
                {/* Name */}
                <span className="font-medium text-yellow-400">{item.name}</span>

                {/* Separator */}
                <span className="mx-1 text-white/30">•</span>

                {/* Message */}
                <span className="font-normal text-white">
                  {item.role} joined from {item.location}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
